import { useEffect, useState, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import * as Yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFAutocomplete, RHFTextField } from 'src/components/hook-form';
import { getRequest, postRequest } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { formatDate, handleErrorMessages } from '../../risk-register/feching';
import Iconify from 'src/components/iconify';
import { DatePicker } from '@mui/x-date-pickers';
import BankInfoInstantCreate from '../bank-information/bank-info-instant-create';

const GeneralLedgerUpdateModal = ({ open,reloadBankInfo, onClose, reload, currentItem, closeform, chartsAccountList, projectList }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [bankInformation, setBankInformation] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  const NewUserSchema = Yup.object().shape({
    // amount: Yup.number().required('Amount is required'),
    // credit: Yup.string().required('Credit/Debit selection is required'),
    // invoice_date: Yup.date().nullable().required('Invoice date is required'),
  });

  const defaultValues = useMemo(() => ({
    account_chart_id: currentItem?.account_chart || null,
    credit: currentItem?.credit ? 'Credit' : 'Debit',
    debit: currentItem?.debit || '',
    amount: currentItem?.credit || currentItem?.debit || '',
    notes: currentItem?.notes || '',
    invoice_date: currentItem?.invoice_date ? new Date(currentItem.invoice_date) : null,
  }), [currentItem]);

  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues,
  });

  const {
    reset,
    control,
    setValue,
    clearErrors,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const getBankInformation = async () => {
      const data = await getRequest(endpoints.accounting.bank_information.get_list);
      if (data) {
        setBankInformation(data);
      }
    };

    const handleOpen = (e) => {
      e.stopPropagation(); // Prevent the parent form from submitting
      setIsOpen(true);
    };

  useEffect(() => {
    getBankInformation();
    reset(defaultValues);
  }, [currentItem, reset, defaultValues]);

  const time = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const onSubmit = handleSubmit(async (data) => {
    console.log('Form data submitted:', data);
    setLoading(true);
    const formData = new FormData();
    if (currentItem?.id) {
      formData.append('id', currentItem.id);
      formData.append('model_type', currentItem?.model_type);
      formData.append('account_chart_id', data.account_chart_id?.id || '');
      formData.append('invoice_date', `${formatDate(data.invoice_date)} ${time}`);
      formData.append('amount', data.amount);
      formData.append('credit', data.credit === 'Credit' ? data.amount : 0);
      formData.append('debit', data.credit === 'Debit' ? data.amount : 0);
      formData.append('notes', data.notes);

    } else {
      formData.append('project_id', data.project_id?.id || '');
      formData.append('account_chart_id', data.account_chart_id?.id || '');
      formData.append('bank_information_id', data?.bank_information_id?.id || '');
      formData.append('invoice_date', `${formatDate(data.invoice_date)} ${time}`);
      formData.append('amount', data.amount);
      formData.append('credit', data.credit === 'Credit' ? data.amount : 0);
      formData.append('debit', data.credit === 'Debit' ? data.amount : 0);
      formData.append('notes', data.notes);
    }


    try {
      const response = await postRequest(
        currentItem?.id
          ? `${endpoints.accounting.general_ledger.upadte_list}`
          : endpoints.accounting.general_ledger.post_list,
        formData,
        true
      );
      if (response.metadata && response.metadata.success) {
        reset();
        onClose();
        closeform();
        reload();
        enqueueSnackbar({
          variant: 'success',
          message: response.metadata.message,
        });
      } else {
        handleErrorMessages(response?.message || 'An error occurred.');
      }
    } catch (err) {
      console.error('Error:', err);
      handleErrorMessages(err.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }
  });
  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      fullWidth
      maxWidth="md"
      sx={{
        borderRadius: 'none',
        padding: '60px',
        fontSize: '13px',
        fontFamily: 'arial',
      }}
      transitionDuration={{
        enter: theme.transitions.duration.shortest,
        exit: theme.transitions.duration.shortest - 80,
      }}
    >
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxSizing: 'content-box' }}>
          <span className='cardColor' style={{ padding: '7px', marginLeft: '-25px', color: 'white', fontWeight: '700' }}>
            &nbsp;&nbsp; {currentItem.id ? " General Ledger Update" : "General Ledger Create"} &nbsp;&nbsp;&nbsp;&nbsp;
          </span>
          <IconButton onClick={onClose}>
            <Iconify icon="mdi:close" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ borderRadius: '1px' }}>
          <Box mt={2} rowGap={3} columnGap={2} display="grid" gridTemplateColumns={{ xs: 'repeat(2, 2fr)', sm: 'repeat(2, 2fr)' }}>

            {currentItem.id ? null
              : (
                <RHFAutocomplete
                  name="project_id"
                  label="Project Name"
                  options={projectList}
                  getOptionLabel={(option) => option?.name || ''}
                  isOptionEqualToValue={(option, value) => option === value}
                  renderOption={(props, option) => <li {...props} key={option.id}>{option.name}</li>}
                />
              )
            }
            <RHFAutocomplete
              name="account_chart_id"
              label="Charts of Account"
              options={chartsAccountList}
              getOptionLabel={(option) =>
                option && option.code && option.name && option.account_type?.name
                  ? `${option.code} - ${option.name} - ${option.account_type.name}`
                  : ''
              }
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.code} - {option.name} - {option.account_type?.name}
                </li>
              )}
            />

        
                <Controller
                  name="invoice_date"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <DatePicker
                      label="Date of Invoice"
                      {...field}
                      value={field.value instanceof Date && !isNaN(field.value) ? field.value : null}
                      onChange={(date) => field.onChange(date || null)}
                      inputFormat="yyyy-MM-dd"
                      renderInput={(params) => (
                        <TextField {...params} fullWidth error={!!error} helperText={error?.message} />
                      )}
                    />
                  )}
                />
   
            <RHFTextField name="amount" type="number" label="Amount" />
            <RHFAutocomplete
              name="credit"
              label="Credit/Debit"
              options={['Credit', 'Debit']}
              getOptionLabel={(option) => option}
              isOptionEqualToValue={(option, value) => option === value}
              renderOption={(props, option) => (
                <li {...props} key={option}>
                  {option}
                </li>
              )}
              onChange={(_, value) => {
                methods.setValue('credit', value);
              }}
              error={!!methods.formState.errors.credit}
              helperText={methods.formState.errors.credit?.message}
            />
            {!currentItem?.id &&  <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
              <RHFAutocomplete
                sx={{ width: '100%' }}
                name="bank_information_id"
                label="Name of Bank"
                options={bankInformation}
                onChange={(_, value) => {
                  setValue('bank_information_id', value);
                  clearErrors('bank_information_id');
                }}
                getOptionLabel={(option) => option?.bank_name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.bank_name}
                  </li>
                )}
              />
              <IconButton
                onClick={handleOpen}
                className="cardColor"
                sx={{
                  margin: '5px 10px',

                  color: '#FFF',
                  fontSize: 25,
                  padding: '7px',
                  borderRadius: '50%',
                  '&:hover': {
                    backgroundColor: '#7AB13A',
                  },
                  boxShadow: '0 3px 5px rgba(0, 0, 0, 0.2)',
                }}
              >
                <Iconify icon="mingcute:add-line" />
              </IconButton>
            </Box>}
          </Box>
          <Box mt={2} rowGap={2} columnGap={2} display="grid" gridTemplateColumns={{ xs: 'repeat(1, 1fr)', sm: 'repeat(1, 1fr)' }}>
            <RHFTextField name="notes" label="Note / Remarks" multiline minRows={3} fullWidth />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton type="submit" variant="contained" className='dark-button' loading={isSubmitting}>
            {!currentItem || !currentItem.id ? 'Create' : 'Update'}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
      <BankInfoInstantCreate
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        reloadBankInfo={()=> getBankInformation()}
      />
    </Dialog>
  );
};

export default GeneralLedgerUpdateModal;
